import { Fragment, ReactNode, useCallback } from 'react';

/**
 * Provides a utility function to wrap array output from `formatMessage` rich-text
 * calls in keyed Fragments, resolving React 19 unique key warnings.
 *
 * When `formatMessage` (react-intl) is called with rich-text values, it returns a
 * `ReactNode[]` whose elements lack keys. React 19 requires sibling nodes in arrays
 * to have unique keys, causing console warnings without this workaround.
 *
 * This is a temporary compatibility shim tracked in EVE-11462.
 * See: https://github.com/formatjs/formatjs/issues/4782
 *
 * Usages of this hook (and the associated `...chunks: ReactNode[]` spread patterns
 * in `formatMessage` tag functions) can be removed once `react-intl` is upgraded to
 * a version that natively supports React 19 (≥ 8.0.9).
 *
 * @example
 * const { wrapNodesWithKeys } = useReact19UniqueKeyCompatibility();
 * return (
 *   <p>
 *     {wrapNodesWithKeys(
 *       formatMessage(message, { Link: (...chunks) => <a>{chunks}</a> }) as ReactNode[]
 *     )}
 *   </p>
 * );
 */
export const useReact19UniqueKeyCompatibility = () => {
    const wrapNodesWithKeys = useCallback(
        (nodes: ReactNode[]): ReactNode =>
            nodes.map((node, index) => <Fragment key={index}>{node}</Fragment>),
        []
    );

    return { wrapNodesWithKeys };
};
